import * as Yup from "yup";

export const SELECT_STYLE_VALIDATION_STR_REGEX = /^\^\(([a-zA-Z0-9\s|)]+)\)\$$/; // Will match something like "^(Checking|Savings)$", and can be used to pull the options values

export const getSelectStyleOptionsFromValidationStr = (validationStr) => {
  if (typeof validationStr != "string") return null;
  const matches = validationStr.match(SELECT_STYLE_VALIDATION_STR_REGEX);
  if (matches && matches.length > 1) {
    const optionsStr = matches[1];
    return optionsStr.split("|")
  } else {
    return null;
  }
};

export const buildInitialValues = (requiredFields = [], countryCode = "US") => {
  const initVals = requiredFields.reduce((acc, f) => {
    let val = f.field_value ? f.field_value : "";
    if (f.request_key === "country") {
      val = countryCode;
    } else if (SELECT_STYLE_VALIDATION_STR_REGEX.test(f.validation)) {
      // select style options, set to first value
      const selectOptions = getSelectStyleOptionsFromValidationStr(f.validation);
      val = Array.isArray(selectOptions) && selectOptions.length > 0 ? selectOptions[0] : "";
    }
    acc[f.request_key] = val;
    return acc;
  }, {});
  initVals.bank_country = countryCode; // the required 'country' code is referring to the country of residence, this holds the bank account country
  return initVals;
};

export const buildSchema = (requiredFields = [], formatMessage) => {
  const requiredErrMsg = formatMessage({
    defaultMessage: "Required",
    description: "Error message for when an input field is missing a required value",
  });
  const DEFAULT_VALIDATION_RULE = Yup.string().required(requiredErrMsg);
  const DEFAULT_VALIDATIONS_BY_KEY = {
    email: Yup.string()
      .required("Required")
      .email(
        formatMessage({
          defaultMessage: "Email must be a valid email",
          description: "Error message for when an input field fails validation",
        }),
      ),
  };
  const yupDefs = requiredFields.reduce((acc, f) => {
    let validationRule = DEFAULT_VALIDATION_RULE;
    if (f.validation) {
      validationRule = Yup.string()
        .required(requiredErrMsg)
        .matches(
          new RegExp(f.validation), // eslint-disable-line security/detect-non-literal-regexp
          formatMessage({
            defaultMessage: "Invalid value",
            description: "Error message for when an input field is missing a required value",
          }),
        );
    } else if (DEFAULT_VALIDATIONS_BY_KEY[f.request_key]) {
      validationRule = DEFAULT_VALIDATIONS_BY_KEY[f.request_key];
    }
    acc[f.request_key] = validationRule;
    return acc;
  }, {});
  const schema = Yup.object().shape(yupDefs);
  return schema;
};

export const REQUIRED_AUTOCOMPLETE_ADDRESS_FIELDS = ["address", "city", "region", "postal_code"];

/**
 * parseRequiredFieldsForAddressAutocomplete
 * To be able to use Google Places Autocomplete we first need
 * to know if the requiredFields payload supports what we know
 * we can handle in what we get from Places autocomplete
 * selection.
 * If the payload does include all the fields we can handle, we
 * prepare a payload so we can separate out what autocomplete fills in,
 * country and everything else into an 'otherFields' param.
 * If the requiredFields payload does not conform, we return null
*/
export const parseRequiredFieldsForAddressAutocomplete = (requiredFields) => {
  let countryField = null;
  let addressFields = [];
  let otherFields = [];
  (requiredFields || []).forEach(rf => {
    if (rf.request_key === "country") {
      countryField = rf;
    } else if (REQUIRED_AUTOCOMPLETE_ADDRESS_FIELDS.includes(rf.request_key)) {
      addressFields.push(rf);
    } else {
      otherFields.push(rf);
    }
  });
  if (countryField === null || addressFields.length !== REQUIRED_AUTOCOMPLETE_ADDRESS_FIELDS.length) {
    // there's a mismatch of what autcomplete can handle and what is in the required fields payload
    // so we can't use autocomplete and should just return null
    return null;
  }
  return { countryField, addressFields, otherFields };
};

export const fieldLabelLookup = (formatMessage) => ({
  Address1: formatMessage({
    defaultMessage: "Address",
    description: "input label",
  }),
  BankAccountBranchNumber: formatMessage({
    defaultMessage: "Bank account branch number",
    description: "input label",
  }),
  BankAccountNumber: formatMessage({
    defaultMessage: "Bank account number",
    description: "input label",
  }),
  BankAccountType: formatMessage({
    defaultMessage: "Bank account type",
    description: "input label",
  }),
  BankRoutingNumber: formatMessage({
    defaultMessage: "Bank routing number",
    description: "input label",
  }),
  BillReferenceNumber: formatMessage({
    defaultMessage: "Bank reference number",
    description: "input label",
  }),
  BirthCountry: formatMessage({
    defaultMessage: "Bank account type",
    description: "input label",
  }),
  City: formatMessage({
    defaultMessage: "City",
    description: "input label",
  }),
  Country: formatMessage({
    defaultMessage: "Country",
    description: "Section title",
  }),
  DateOfBirth: formatMessage({
    defaultMessage: "Date of birth",
    description: "input label",
  }),
  PostalCode: formatMessage({
    defaultMessage: "Postal Code",
    description: "input label",
  }),
  SWIFT: formatMessage({
    defaultMessage: "Swift",
    description: "input label",
  }),
  FirstName: formatMessage({
    defaultMessage: "First name",
    description: "input label",
  }),
  LastName: formatMessage({
    defaultMessage: "Last name",
    description: "Input field label",
  }),
  Address: formatMessage({
    defaultMessage: "Address",
    description: "Section title",
  }),
  Region: formatMessage({
    defaultMessage: "Region",
    description: "input label",
  }),
});

export const fieldLevelApiErrorDetailsLookup = (formatMessage) => {
  return {
    INVALID_IBAN: {
      request_key: "iban",
      message: formatMessage({
        defaultMessage: "Invalid IBAN number",
        description: "error message description",
      })
    },
    INVALID_CURRENCY: {
      request_key: "bank_account_currency",
      message: formatMessage({
        defaultMessage: "Invalid currency code",
        description: "error message description",
      })
    },
    INVALID_FIRST_NAME: {
      request_key: "first_name",
      message: formatMessage({
        defaultMessage: "Invalid name value",
        description: "error message description",
      })
    },
    INVALID_LAST_NAME: {
      request_key: "last_name",
      message: formatMessage({
        defaultMessage: "Invalid name value",
        description: "error message description",
      })
    },
    INVALID_ADDRESS: {
      request_key: "address",
      message: formatMessage({
        defaultMessage: "Invalid address value",
        description: "error message description",
      })
    },
    INVALID_CITY: {
      request_key: "city",
      message: formatMessage({
        defaultMessage: "Invalid city",
        description: "error message description",
      })
    },
    INVALID_REGION: {
      request_key: "region",
      message: formatMessage({
        defaultMessage: "Invalid region",
        description: "error message description",
      })
    },
    INVALID_POSTAL_CODE: {
      request_key: "postal_code",
      message: formatMessage({
        defaultMessage: "Invalid postal code",
        description: "error message description",
      })
    },
    INVALID_COUNTRY: {
      request_key: "country",
      message: formatMessage({
        defaultMessage: "Invalid country",
        description: "error message description",
      })
    },
  };
};

export const parseFinishCreateErrorForFieldLevelCodes = (tiliaAPIError, formatMessage) => {
  let response = null;
  const errorLookup = fieldLevelApiErrorDetailsLookup(formatMessage);
  try {
    const lookupByFieldKey = (tiliaAPIError?.data?.codes || []).reduce(
      (accumulator, code) => {
        const errDetails = errorLookup[code];
        if (errDetails) {
          if (accumulator[errDetails.request_key]) {
            accumulator[errDetails.request_key].push(errDetails.message);
          } else {
            accumulator[errDetails.request_key] = [errDetails.message];
          }
        }
        return accumulator;
      },
      {},
    );
    if (Object.keys(lookupByFieldKey).length > 0) {
      // we have at least one error code
      response = lookupByFieldKey;
    }
  } catch (_err) { /* noop */ }
  return response;
};

export const addressOnlyrequiredFields = [
  {
    "label": "First Name",
    "expected_value": "",
    "validation": "",
    "request_key": "first_name",
    "type": "FirstName",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "Last Name",
    "expected_value": "",
    "validation": "",
    "request_key": "last_name",
    "type": "LastName",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "Address",
    "expected_value": "",
    "validation": "",
    "request_key": "address",
    "type": "Address",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "City",
    "expected_value": "",
    "validation": "",
    "request_key": "city",
    "type": "City",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "Region",
    "expected_value": "",
    "validation": "",
    "request_key": "region",
    "type": "Region",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "Postal Code",
    "expected_value": "",
    "validation": "",
    "request_key": "postal_code",
    "type": "PostalCode",
    "hidden": false,
    "field_value": ""
  },
  {
    "label": "Country",
    "expected_value": "",
    "validation": "",
    "request_key": "country",
    "type": "Country",
    "hidden": false,
    "field_value": ""
  }
];
